import { FC, ReactNode, useCallback, useRef, useState } from 'react';
import { Layer, NumberInput } from '@carbon/react';
import { useAtomValue } from 'jotai';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { TypeCurveProbitPlots } from './type-curve-probit-plots';
import type { FilteredWell } from '../types';
import { TypeCurveVolumeCharts } from './type-curve-volume-charts';
import { TypeCurveCrossPlot } from './type-curve-cross-plot';
import {
  tcMonthlyVolumesColorMapAtom,
  tcRatiosColorMapAtom,
  tcCrossPlotColorMapAtom,
} from '../type-curve-filters/atoms';
import { MineralType } from 'types/minerals';
import type { DcaForecastParameter } from 'types/api-responses';
import { TcDcaModal } from '../type-curve-dca/tc-dca-modal';
import styles from './type-curve-charts-section.module.scss';

const MIN_LAYOUT = 1;
const MAX_LAYOUT = 10;

export interface TypeCurveChartsSectionProps {
  /** Required by the probit API for TC scaling + P-value context. */
  typeCurveHeaderDetailsId: number | null;
  /** Required by the probit API. Pass the active / default scenario ID. */
  dcaScenarioId: number;
  /**
   * Optional pre-resolved filtered wells (TC pages filter upstream via
   * POST /queries/wells). Each entry carries `uwi` + `name` + `wellQualifier`
   * so downstream probit tooltips can render well context without a separate
   * lookup. When omitted the backend falls back to workspace/UWI-free
   * resolution.
   */
  wells?: FilteredWell[];
  /** Accordion header label. Defaults to "Charts". */
  title?: ReactNode;
  /** Whether the accordion is open by default. Defaults to `true`. */
  defaultOpen?: boolean;
  lateralLengthFt: number | null;
  typeCurveDateCreated: string | null;
  structureGridCollectionId?: number | null;
  /** Type curve area name for the DCA modal's TC selector. */
  typeCurveAreaName: string;
  /** DCA forecast parameters from the header (used for promoted workspace prefill). */
  dcaForecastParameters?: DcaForecastParameter[];
  proppantIntensityLbsFt: number | null;
  fluidIntensityBblFt: number | null;
  fluidIntensityGalFt: number | null;
  horizontalWellSpacingFt: number | null;
  /** Default aggregation type from typeWellDefaults (already converted to camelCase). */
  defaultAggregation?: string;
  /** DCA scenario ID from typeWellDefaults — used only in the DCA modal to pre-select the scenario. */
  typeWellDefaultDcaScenarioId?: number | null;
}

// Self-contained charts accordion that renders the probit plots and
// multi-well volume charts for a type curve.
export const TypeCurveChartsSection: FC<TypeCurveChartsSectionProps> = ({
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  wells,
  title = 'Charts',
  defaultOpen = true,
  lateralLengthFt,
  typeCurveDateCreated,
  structureGridCollectionId,
  typeCurveAreaName,
  dcaForecastParameters,
  proppantIntensityLbsFt,
  fluidIntensityBblFt,
  fluidIntensityGalFt,
  horizontalWellSpacingFt,
  defaultAggregation,
  typeWellDefaultDcaScenarioId,
}) => {
  const [rows, setRows] = useState(2);
  const [columns, setColumns] = useState(3);

  // DCA modal state
  const [dcaModalOpen, setDcaModalOpen] = useState(false);
  const [dcaStream, setDcaStream] = useState<MineralType | null>(null);

  const handleEditStream = useCallback((stream: MineralType) => {
    setDcaStream(stream);
    setDcaModalOpen(true);
  }, []);

  const handleDcaModalClose = useCallback(() => {
    setDcaModalOpen(false);
    setDcaStream(null);
  }, []);

  // Color-by-attribute UWI → color maps for the volume / ratio / cross plot charts.
  // Probit's own map is read inside `TypeCurveProbitPlots`.
  const monthlyVolumesColorMap = useAtomValue(tcMonthlyVolumesColorMapAtom);
  const ratiosColorMap = useAtomValue(tcRatiosColorMapAtom);
  const crossPlotColorMap = useAtomValue(tcCrossPlotColorMapAtom);

  // Shared cross-chart hover — hovering a point/line in any chart highlights
  // the same UWI across all probit and volume charts simultaneously.
  const [hoveredUwi, setHoveredUwi] = useState<string | null>(null);
  const nullHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChartHover = useCallback((uwi: string | null) => {
    if (nullHoverTimerRef.current) {
      clearTimeout(nullHoverTimerRef.current);
      nullHoverTimerRef.current = null;
    }
    if (uwi) {
      setHoveredUwi(uwi);
    } else {
      // 50ms debounce on null to prevent flicker when moving between data points
      nullHoverTimerRef.current = setTimeout(() => setHoveredUwi(null), 50);
    }
  }, []);

  return (
    <>
      <Accordion defaultExpanded={defaultOpen}>
        <AccordionSummary>
          <h2 className={styles.chartsAccordionTitle}>{title}</h2>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.chartsAccordionContent}>
            <div className={styles.layoutGroup}>
              <div className={styles.layoutInputWrapper}>
                <Layer>
                  <NumberInput
                    id="tc-charts-layout-rows"
                    size="md"
                    min={MIN_LAYOUT}
                    max={MAX_LAYOUT}
                    value={rows}
                    label="Rows"
                    onChange={(
                      _e: unknown,
                      { value }: { value: string | number }
                    ) =>
                      setRows(
                        Math.min(
                          MAX_LAYOUT,
                          Math.max(MIN_LAYOUT, Number(value))
                        )
                      )
                    }
                    className={styles.layoutInput}
                    hideSteppers
                  />
                </Layer>
              </div>
              <div className={styles.layoutInputWrapper}>
                <Layer>
                  <NumberInput
                    id="tc-charts-layout-columns"
                    size="md"
                    label="Columns"
                    min={MIN_LAYOUT}
                    max={MAX_LAYOUT}
                    value={columns}
                    onChange={(
                      _e: unknown,
                      { value }: { value: string | number }
                    ) =>
                      setColumns(
                        Math.min(
                          MAX_LAYOUT,
                          Math.max(MIN_LAYOUT, Number(value))
                        )
                      )
                    }
                    className={styles.layoutInput}
                    hideSteppers
                  />
                </Layer>
              </div>
            </div>

            <div
              className={styles.chartsGrid}
              style={{
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              }}
            >
              <TypeCurveProbitPlots
                typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
                dcaScenarioId={dcaScenarioId}
                wells={wells}
                highlightedUwi={hoveredUwi}
                onPointHover={handleChartHover}
              />
              <TypeCurveVolumeCharts
                wells={wells ?? []}
                typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
                dcaScenarioId={dcaScenarioId}
                highlightedUwi={hoveredUwi}
                onWellHover={handleChartHover}
                monthlyVolumesColorMap={monthlyVolumesColorMap}
                ratiosColorMap={ratiosColorMap}
                onEditStream={handleEditStream}
                defaultAggregation={defaultAggregation}
              />
              <TypeCurveCrossPlot
                wells={wells ?? []}
                typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
                dcaScenarioId={dcaScenarioId}
                lateralLengthFt={lateralLengthFt}
                uwiColorMap={crossPlotColorMap}
                highlightedUwi={hoveredUwi}
                onPointHover={handleChartHover}
                typeCurveDateCreated={typeCurveDateCreated}
                structureGridCollectionId={structureGridCollectionId}
                proppantIntensityLbsFt={proppantIntensityLbsFt}
                fluidIntensityBblFt={fluidIntensityBblFt}
                fluidIntensityGalFt={fluidIntensityGalFt}
                horizontalWellSpacingFt={horizontalWellSpacingFt}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <TcDcaModal
        open={dcaModalOpen}
        stream={dcaStream}
        onClose={handleDcaModalClose}
        dcaScenarioId={dcaScenarioId}
        wells={wells ?? []}
        typeCurveAreaName={typeCurveAreaName}
        typeCurveHeaderDetailsId={typeCurveHeaderDetailsId}
        dcaForecastParameters={dcaForecastParameters}
        defaultAggregation={defaultAggregation}
        typeWellDefaultDcaScenarioId={typeWellDefaultDcaScenarioId}
      />
    </>
  );
};
